// Pinterest OAuth callback landing page.
// Users arrive here after Pinterest redirects them back through
// /api/brand-os/publish/oauth/pinterest/callback. One access token covers a Business account; the picker shows the
// writeable BOARDS so the connection targets a specific board, so this is a one-row confirmation picker.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function humanizeError(code) {
  const m = {
    missing_code_or_state: "The redirect from Pinterest was missing the code or state parameter.",
    state_expired: "The connect flow timed out before it finished. Please start again.",
    state_malformed: "Something altered the OAuth state. Please start the connect flow again.",
    state_invalid_signature: "The OAuth state failed its signature check. Please start the connect flow again.",
    state_channel_mismatch: "That OAuth state wasn't issued for Pinterest.",
    code_exchange_failed: "Pinterest refused the authorization code. Please try the connect flow again.",
    userinfo_failed: "Pinterest accepted the token but refused to describe the account.",
    tiktok_unreachable: "We couldn't reach Pinterest. Try again in a minute.",
  };
  return m[code] || code;
}

// ---------------------------------------------------------------------
// Create-board form
// ---------------------------------------------------------------------
// Pinterest's sandbox is a separate data space from the real account,
// so trial-tier apps see "no writeable boards" after connecting even
// when the account has many boards. This form posts
// `POST /v5/boards` on the configured PINTEREST_API_BASE using the
// in-flight OAuth session's access token, then adds the fresh board
// to the picker and auto-selects it. Doubles as the `boards:write`
// demo surface for the Pinterest review video.

function CreateBoardForm({ sessionId, sandbox, onCreated }) {
  const [name, setName] = useState("Brand OS test");
  const [description, setDescription] = useState(
    "Sandbox board used for the Pinterest API review walkthrough.",
  );
  const [privacy, setPrivacy] = useState("PUBLIC");
  const [status, setStatus] = useState({ kind: "idle" });

  async function submit(e) {
    e?.preventDefault?.();
    setStatus({ kind: "sending" });
    try {
      const r = await api.post(
        `/publish/oauth/pinterest/session/${encodeURIComponent(sessionId)}/create-board`,
        {
          session_id: sessionId,
          name: name.trim(),
          description: description.trim() || null,
          privacy,
        },
      );
      setStatus({ kind: "success", board: r.data?.board });
      if (onCreated && r.data?.board) onCreated(r.data.board);
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setStatus({
        kind: "error",
        message: d.message || formatApiError(e),
        pinterestError: d.pinterest || null,
      });
    }
  }

  return (
    <form
      onSubmit={submit}
      data-testid="pn-create-board-form"
      style={{
        marginTop: 16,
        padding: 14,
        border: "1px dashed var(--bo-border)",
        borderRadius: 8,
        background: "var(--bo-panel-alt, #faf8f2)",
      }}
    >
      <div className="bo-eyebrow" style={{ fontSize: 10 }}>
        Create a board
      </div>
      <p style={{ marginTop: 6, marginBottom: 10, fontSize: 13,
                  color: "var(--bo-muted)", lineHeight: 1.5 }}>
        No boards here yet? Create one now. {sandbox
          ? "You're on Pinterest's trial sandbox, which is a separate data space from your public account — it won't show your real boards."
          : "This posts to your real Pinterest account."}
      </p>
      <div style={{ display: "grid", gap: 10,
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <div>
          <label className="bo-label" htmlFor="pn-cb-name">Board name</label>
          <input
            id="pn-cb-name"
            className="bo-input"
            maxLength={180}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={status.kind === "sending"}
            data-testid="pn-create-board-name"
            style={{ width: "100%", boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label className="bo-label" htmlFor="pn-cb-privacy">Privacy</label>
          <select
            id="pn-cb-privacy"
            className="bo-input"
            value={privacy}
            onChange={(e) => setPrivacy(e.target.value)}
            disabled={status.kind === "sending"}
            data-testid="pn-create-board-privacy"
            style={{ width: "100%" }}
          >
            <option value="PUBLIC">Public</option>
            <option value="SECRET">Secret</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label className="bo-label" htmlFor="pn-cb-desc">
          Description (optional, {description.length}/500)
        </label>
        <textarea
          id="pn-cb-desc"
          className="bo-input"
          rows={2}
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={status.kind === "sending"}
          data-testid="pn-create-board-description"
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
        />
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="submit"
          className="bo-btn bo-btn-primary"
          disabled={!name.trim() || status.kind === "sending"}
          data-testid="pn-create-board-submit"
        >
          {status.kind === "sending" ? "Creating…" : "Create board"}
        </button>
        {status.kind === "success" && (
          <span style={{ fontSize: 13, color: "#1f6c3f" }}
                data-testid="pn-create-board-success">
            Created "{status.board?.title}" · selected below.
          </span>
        )}
      </div>
      {status.kind === "error" && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 6,
                      background: "#fee2d2", color: "#8a2b0a" }}
             role="alert"
             data-testid="pn-create-board-error">
          <strong>Create board failed.</strong> {status.message}
          {status.pinterestError && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--bo-muted)" }}>
              Pinterest: <code>
                {JSON.stringify(status.pinterestError).slice(0, 240)}
              </code>
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default function PinterestOAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session") || "";
  const errorFromPinterest = params.get("error");

  const [session, setSession] = useState(null);
  const [pickedId, setPickedId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [submitState, setSubmitState] = useState({ submitting: false, error: null });

  const bounceBack = useCallback((flash, err) => {
    const search = new URLSearchParams();
    if (flash) search.set("oauth_result", encodeURIComponent(flash));
    if (err) search.set("oauth_error", encodeURIComponent(err));
    navigate({
      pathname: "/brand-os/admin/channel-connections",
      search: `?${search.toString()}`,
    }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (errorFromPinterest) {
      setLoadState({ loading: false, error: humanizeError(errorFromPinterest) });
      return;
    }
    if (!sessionId) {
      setLoadState({ loading: false, error: "Missing session id in URL." });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/publish/oauth/pinterest/session/${sessionId}`);
        setSession(r.data);
        const accounts = r.data.pages || [];
        if (accounts.length >= 1) {
          setPickedId(String(accounts[0].id));
          setDisplayName(accounts[0].title ? `Pinterest · ${accounts[0].title}` : "");
        }
        setLoadState({ loading: false, error: null });
      } catch (e) {
        setLoadState({ loading: false, error: formatApiError(e) });
      }
    })();
  }, [sessionId, errorFromPinterest]);

  const accounts = session?.pages || [];
  const diagnostic = session?.diagnostic || {};
  const sandbox = Boolean(diagnostic?.pinterest_sandbox
                          || diagnostic?.api_base?.includes("api-sandbox"));

  // Callback that fires after `POST .../create-board` succeeds — we
  // append the new board to the picker list and select it.
  function handleBoardCreated(board) {
    const existingIdx = accounts.findIndex(
      (a) => String(a.id) === String(board.id),
    );
    const nextPages = existingIdx >= 0
      ? accounts
      : [...accounts, {
          id: board.id,
          title: board.title,
          privacy_status: board.privacy_status,
          pin_count: board.pin_count || 0,
          custom_url: null,
          thumbnail_url: null,
        }];
    setSession({ ...(session || {}), pages: nextPages });
    setPickedId(String(board.id));
    if (!displayName.trim()) {
      setDisplayName(`Pinterest · ${board.title}`);
    }
  }

  async function handleFinalize() {
    if (!pickedId) return;
    setSubmitState({ submitting: true, error: null });
    try {
      const r = await api.post("/publish/oauth/pinterest/finalize", {
        session_id: sessionId,
        channel_id: pickedId,
        display_name: displayName.trim(),
        is_default: isDefault,
      });
      const name = r.data?.connection?.display_name || "Pinterest account";
      bounceBack(`Connected ${name}.`);
    } catch (e) {
      setSubmitState({ submitting: false, error: formatApiError(e) });
    }
  }

  if (loadState.loading) {
    return (
      <Shell title="Connect Pinterest">
        <LoadingCard label="Loading the Pinterest OAuth session…" />
      </Shell>
    );
  }

  // Diagnostic details: never emits the access token. We keep a static
  // allowlist of fields so a future bug can't accidentally leak secrets.
  const safeDiagnostic = {
    session_id: sessionId,
    channel: session?.channel,
    state: session?.state,
    granted_scopes: diagnostic.granted_scopes,
    required_scopes: diagnostic.required_scopes,
    refresh_token_present: diagnostic.refresh_token_present,
    pinterest_username: diagnostic.pinterest_username,
    pinterest_app_access_tier: diagnostic.pinterest_app_access_tier,
    pinterest_sandbox: sandbox,
    api_base: diagnostic.api_base,
  };

  return (
    <Shell title="Connect Pinterest">
      <div className="bo-page" data-testid="pn-oauth-callback">
        <section className="bo-card">
          <div className="bo-eyebrow">Publish → Channel Connections → Pinterest</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Pick the Pinterest board to connect
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 760, lineHeight: 1.55 }}>
            Pinterest issued a 30-day access token and a long-lived refresh token.
            Brand OS keeps both encrypted and refreshes automatically before expiry.
          </p>

          {sandbox && (
            <Pill data-testid="pn-sandbox-pill" tone="warn" style={{ marginTop: 8 }}>
              Trial access: pins are created in Pinterest's sandbox for review purposes.
            </Pill>
          )}

          {loadState.error ? (
            <ErrorBanner data-testid="pn-oauth-error">{loadState.error}</ErrorBanner>
          ) : null}

          {diagnostic.refresh_token_warning ? (
            <ErrorBanner data-testid="pn-refresh-warning" tone="warn">
              {diagnostic.refresh_token_warning}
            </ErrorBanner>
          ) : null}

          {accounts.length === 0 ? (
            <EmptyState
              title="Pinterest returned no writeable boards for this account"
              detail={sandbox
                ? "The Pinterest sandbox is a separate data space from your public account, so it won't show your real boards. Create a sandbox board below."
                : "Make sure you signed in with the Pinterest Business account that owns @precipice.health."}
              data-testid="pn-no-accounts"
            />
          ) : null}

          <CreateBoardForm
            sessionId={sessionId}
            sandbox={sandbox}
            onCreated={handleBoardCreated}
          />

          {accounts.length > 0 && (
            <>
              <div style={{ display: "grid", gap: 8, margin: "16px 0" }}>
                {accounts.map((acc) => (
                  <label
                    key={acc.id}
                    data-testid={`pn-board-opt-${acc.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 12,
                      border: "1px solid var(--bo-border)",
                      borderRadius: 6,
                      cursor: "pointer",
                      background:
                        pickedId === String(acc.id)
                          ? "var(--bo-panel-alt)"
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="pn-account"
                      value={acc.id}
                      checked={pickedId === String(acc.id)}
                      onChange={(e) => setPickedId(e.target.value)}
                    />
                    {acc.thumbnail_url ? (
                      <img
                        src={acc.thumbnail_url}
                        alt=""
                        width={48}
                        height={48}
                        style={{ borderRadius: "50%" }}
                      />
                    ) : null}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <strong>{acc.title}</strong>
                      <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                        {acc.custom_url || acc.id}
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              <div className="bo-field" style={{ marginTop: 8 }}>
                <label className="bo-label" htmlFor="pn-display-name">
                  Connection display name
                </label>
                <input
                  id="pn-display-name"
                  className="bo-input"
                  data-testid="pn-display-name-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Pinterest · Precipice Health"
                />
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 10,
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  data-testid="pn-is-default-checkbox"
                />
                Make this the default Pinterest connection for scheduled posts
              </label>

              {submitState.error ? (
                <ErrorBanner data-testid="pn-finalize-error">
                  {submitState.error}
                </ErrorBanner>
              ) : null}

              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button
                  className="bo-btn bo-btn-primary"
                  data-testid="pn-finalize-btn"
                  onClick={handleFinalize}
                  disabled={!pickedId || submitState.submitting}
                >
                  {submitState.submitting ? "Connecting…" : "Connect this board"}
                </button>
                <button
                  className="bo-btn bo-btn-ghost"
                  data-testid="pn-cancel-btn"
                  onClick={() => bounceBack(null, "User cancelled")}
                  disabled={submitState.submitting}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          <details style={{ marginTop: 24 }}>
            <summary
              style={{ cursor: "pointer", color: "var(--bo-muted)", fontSize: 13 }}
            >
              Diagnostic details
            </summary>
            <pre
              style={{
                fontSize: 12,
                background: "var(--bo-panel-alt)",
                padding: 10,
                borderRadius: 4,
                overflow: "auto",
              }}
              data-testid="pn-diagnostic-pre"
            >
              {JSON.stringify(safeDiagnostic, null, 2)}
            </pre>
          </details>
        </section>
      </div>
    </Shell>
  );
}
